import {
  clearMyInventory,
  getCrateBalance,
  getMyInventoryItems,
  getOgNFTBalance,
} from "@/features/inventory";
import Box from "@mui/material/Box";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DeadAccountFullscreenGate,
  DeadAccountFullscreenLayout,
} from "@/features/rebirth";
import BackOverlay from "./components/BackOverlay";
import ChatPanel from "./components/ChatPanel";
import RateLimitPopup from "./components/RateLimitPopup";
import { SideChatPanel } from "@/features/inbox";
import HeaderV2 from "./HeaderV2";
import MobileSideBar from "./MobileSideBar";
import SideBar from "./SideBar";

import { getCitiesTotalPowerAction } from "@/features/equipment";
import {
  clearDetectives,
  getDetectiveSidebarBadge,
  getDetectiveListAction,
} from "@/features/detective-agency";
import { getNextCrushTimeAction } from "@/features/car-crusher";
import { getNextRaceTimeAction, RaceResultListener } from "@/features/race";
import { getNextTrainTimeAction } from "@/features/kill-skill";
import { getNextCrimeTimeAction } from "@/features/crime";
import { getNextNickCarAction } from "@/features/nick-car";
import {
  getStoryModeUserMissionStatusAction,
  getUserMissionStatusAction,
} from "@/features/story";
import { getFamilyList } from "@/features/family";
import {
  getLastTransferTimeAction
} from "@/features/bank";
import {
  getHealthBalance,
  getNextHealthBuyTimeAction
} from "@/features/hospital";
import {
  getBulletBalance,
  getBulletTokenBalance,
  getNextBulletBuyTimeAction
} from "@/features/bullet-factory";
import {
  getNextBoozeTimeAction,
  getNextNarcsTimeAction
} from "@/features/smuggling";
import {
  getJailedUntilAction,
  selectJailedUntil,
} from "@/features/jail";
import { fetchMafiaPrice, fetchNativePrice } from "@/features/token-prices";
import { fetchVaultBalance } from "@/features/vault";
import { getCurrentRoundAction } from "@/features/jackpot";
import { getHelperCreditBalance } from "@/features/helper-bot";
import { getGICreditBalanceAction } from "@/features/game-intelligence";
import { clearNotifications } from "@/features/notifications";
import { getIsInLobbyAction, getNextLobbyTimeAction } from "@/features/organized-crime";
import { getActivePerksAction } from "@/features/perks";
import {
  clearMyProfile,
  getMyProfile,
  getProfileNames,
} from "@/features/profile";
import { getUserTravelInfoAction } from "@/features/travel";
import { getNextBuyTimeAction } from "@/features/shop";
import {
  clearSafehouseInfo,
  getSafehouseUserInfoAction,
  selectSafehouseUntil,
} from "@/features/safehouse";

import config from "@/config/config";
import { switchNetwork } from "@/helper/network";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import useAuth from "@/hook/useAuth";
import useCurrentTime from "@/hook/useCurrentTime";
import useGameBankBalance from "@/hook/useGameBankBalance";
import useWallet from "@/hook/useWallet";

import { shouldShowDeadAccountFullscreen, shouldUseDeadAccountMinimalLayout } from "@/constants/deadAccount";
import { resolveMyProfileIsDead } from "@/helper/profile";
import { shouldRedirectToProfileCreation } from "@/helper/profileAccess";
import { useTermsDialog } from "@/features/terms";
import { JAIL_RESTRICTED_PATHS, SAFEHOUSE_RESTRICTED_PATHS } from "@/constants/jail";
import { toast } from "@/utils/utils";

import { useStyles } from "./index.styles";
import { LayoutChromeProvider, useLayoutChrome } from "./LayoutChromeContext";

const MainLayoutContent = (props: React.PropsWithChildren) => {
  const { classes } = useStyles();
  const { chromeHidden } = useLayoutChrome();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLink = useLocation();
  const currentTime = useCurrentTime();
  const { wallet, connectWallet, disconnectWallet, account } = useWallet();
  const { accepted: termsAccepted } = useTermsDialog();

  useAuth(termsAccepted ? account : "");

  const profileState = useAppSelector((state) => state.profile);
  const { profileNames, myProfile } = profileState;
  const {
    account: authenticatedAccount,
    isAuthorized,
    signMsg,
    signature,
  } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);
  const jailedUntil = useAppSelector(selectJailedUntil);
  const detectives = useAppSelector((state) => state.detective.detectives);

  const rateExceeded = useAppSelector((state) => state.auth.rateExceeded);
  const hasAuthenticatedWallet =
    Boolean(account) &&
    isAuthorized &&
    authenticatedAccount.toLowerCase() === account.toLowerCase() &&
    Boolean(signMsg) &&
    Boolean(signature);
  const detectiveBadge = hasAuthenticatedWallet
    ? getDetectiveSidebarBadge(detectives, currentTime)
    : null;
  const redirectToProfileCreation = termsAccepted && shouldRedirectToProfileCreation(
    account,
    currentLink.pathname,
    profileState
  );

  const [openChat, setOpenChat] = useState(false);
  const [showMobileSideBar, toggleMobileSideBar] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const lastProfileRedirect = useRef<string | null>(null);

  useEffect(() => {
    if (!redirectToProfileCreation) {
      lastProfileRedirect.current = null;
      return;
    }

    const redirectKey = `${account.toLowerCase()}:${currentLink.pathname}`;
    if (lastProfileRedirect.current === redirectKey) return;
    lastProfileRedirect.current = redirectKey;
    toast.info("Please create a profile to continue.");
    navigate("/create-profile", { replace: true });
  }, [account, currentLink.pathname, navigate, redirectToProfileCreation]);

  const onConnectWallet = useCallback(() => {
    setOpenChat(false);
    connectWallet();
  }, [connectWallet]);

  useEffect(() => {
    if (wallet) {
      const checkNetwork = () => {
        window.provider = wallet.provider as typeof window.provider;
        window.wallet = wallet as Window["wallet"];
        if (!termsAccepted) return;
        if (wallet.chains[0].id !== `0x${config.chainIdHex.toString(16)}`) {
          disconnectWallet();
        switchNetwork(wallet.provider as any)
            .then(() => {
              onConnectWallet();
            })
            .catch((error) => {
              disconnectWallet();
            });
        }
      };

      checkNetwork();
    }
  }, [wallet, dispatch, disconnectWallet, onConnectWallet, termsAccepted]);

  useEffect(() => {
    if (!account) return;
    if (!dispatch) return;

    dispatch(getCrateBalance({ account }));
    dispatch(getOgNFTBalance({ account }));
    dispatch(getJailedUntilAction(account));
    dispatch(getActivePerksAction({ account }));
    dispatch(getBulletTokenBalance({ account }));

    dispatch(getNextCrimeTimeAction(account));
    dispatch(getNextNickCarAction(account));
    dispatch(getNextTrainTimeAction(account));
    dispatch(getNextNarcsTimeAction(account));
    dispatch(getNextBoozeTimeAction(account));
    dispatch(getNextBulletBuyTimeAction(account));
    dispatch(getNextHealthBuyTimeAction(account));
    dispatch(getNextRaceTimeAction(account));
    dispatch(getLastTransferTimeAction(account));
    dispatch(getNextBuyTimeAction(account));
    dispatch(getUserMissionStatusAction(account));
    dispatch(getNextCrushTimeAction(account));

    dispatch(getNextLobbyTimeAction(account));
    dispatch(getIsInLobbyAction(account));

    dispatch(getStoryModeUserMissionStatusAction(account));
  }, [account, dispatch]);

  useEffect(() => {
    dispatch(fetchVaultBalance());
    dispatch(fetchNativePrice());
    dispatch(fetchMafiaPrice());

    dispatch(getFamilyList());
    dispatch(getProfileNames());
    dispatch(getCurrentRoundAction());
  }, [dispatch]);

  useEffect(() => {
    if (!account) {
      dispatch(clearMyInventory());
      return;
    }
    if (!hasAuthenticatedWallet) return;
    if (!dispatch) return;
    if (profileNames.length === 0) return;

    const checkProfile = async () => {
      const profile = profileNames.find(
        (name) => name.address?.toLowerCase() === account.toLowerCase()
      );
      const profileName = profile?.name || "";
      const profileId = profile?.id || 0;

      if (profileName && profileId) {
        dispatch(getMyProfile(profileName));
        dispatch(getMyInventoryItems({ userId: profileId }));
        dispatch(clearNotifications());
      } else {
        dispatch(clearMyProfile());
        dispatch(clearMyInventory());
        dispatch(clearNotifications());
      }
    };

    checkProfile();
  }, [account, dispatch, hasAuthenticatedWallet, profileNames]);

  useEffect(() => {
    if (
      !account ||
      !isAuthorized ||
      authenticatedAccount.toLowerCase() !== account.toLowerCase() ||
      !signMsg ||
      !signature
    ) {
      dispatch(clearSafehouseInfo());
      return;
    }

    dispatch(getHelperCreditBalance({ account, signMsg, signature }));
    dispatch(getBulletBalance({ account, signMsg, signature }));
    dispatch(getHealthBalance({ account, signMsg, signature }));
    dispatch(getGICreditBalanceAction({ account, signMsg, signature }));
    dispatch(getUserTravelInfoAction({ account, signMsg, signature }));
    dispatch(getCitiesTotalPowerAction({ account, signMsg, signature }));
    dispatch(getSafehouseUserInfoAction({ account, signMsg, signature }));

    updateBalance(account, signMsg, signature);
  }, [
    account,
    authenticatedAccount,
    dispatch,
    isAuthorized,
    signMsg,
    signature,
    updateBalance,
  ]);

  useEffect(() => {
    // Keep the sidebar aware of completed searches without visiting the agency.
    // Clearing also invalidates any response started for a previous wallet.
    dispatch(clearDetectives());
    if (!hasAuthenticatedWallet) return;

    const refreshDetectives = () => {
      if (!document.hidden) dispatch(getDetectiveListAction());
    };

    refreshDetectives();
    const intervalId = window.setInterval(refreshDetectives, 30_000);
    document.addEventListener("visibilitychange", refreshDetectives);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshDetectives);
    };
  }, [account, dispatch, hasAuthenticatedWallet, signMsg, signature]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const safehouseUntil = useAppSelector(selectSafehouseUntil);
  const isDead = resolveMyProfileIsDead(myProfile);
  const hasLoadedProfile = Boolean(myProfile?.name || myProfile?.id);
  const showDeadFullscreen =
    isDead &&
    hasLoadedProfile &&
    shouldShowDeadAccountFullscreen(currentLink.pathname, isDead);
  const showChrome = !chromeHidden;
  const useMinimalLayout =
    isDead &&
    hasLoadedProfile &&
    shouldUseDeadAccountMinimalLayout(currentLink.pathname, isDead);
  const listenForRaceResult = hasAuthenticatedWallet &&
    currentLink.pathname !== "/racing" &&
    !currentLink.pathname.startsWith("/racing/result/");

  // Prevent navigate to specific paths when in jail (skip while dead — dead fullscreen takes over)
  useEffect(() => {
    if (!termsAccepted || isDead || redirectToProfileCreation) return;

    if (
      currentTime < jailedUntil &&
      JAIL_RESTRICTED_PATHS.some((path) => currentLink.pathname.includes(path))
    ) {
      navigate("/injail");
    }
  }, [currentTime, jailedUntil, navigate, currentLink.pathname, isDead, redirectToProfileCreation, termsAccepted]);

  // Prevent navigate to specific paths when in safehouse
  useEffect(() => {
    if (!termsAccepted || isDead || redirectToProfileCreation) return;

    if (
      safehouseUntil > 0 &&
      currentTime < safehouseUntil &&
      SAFEHOUSE_RESTRICTED_PATHS.some((path) => currentLink.pathname.includes(path))
    ) {
      navigate("/safehouse");
    }
  }, [currentTime, safehouseUntil, navigate, currentLink.pathname, isDead, redirectToProfileCreation, termsAccepted]);

  return (
    <Box className={classes.body}>
      {listenForRaceResult && <RaceResultListener account={authenticatedAccount} />}
      {useMinimalLayout ? (
        showDeadFullscreen ? (
          <DeadAccountFullscreenGate />
        ) : (
          <DeadAccountFullscreenLayout>
            {props.children}
          </DeadAccountFullscreenLayout>
        )
      ) : (
        <>
          {showChrome && <HeaderV2 toggleMobileSideBar={toggleMobileSideBar} />}

          <Box className={showChrome ? classes.container : undefined}>
            {showChrome && windowWidth >= 900 && <SideBar detectiveBadge={detectiveBadge} />}

            <Box className={showChrome ? classes.contentContainer : undefined}>
              {(!account || hasAuthenticatedWallet) && !redirectToProfileCreation && props.children}
            </Box>
          </Box>
        </>
      )}

      {!useMinimalLayout && showChrome && (
        <MobileSideBar
          detectiveBadge={detectiveBadge}
          showMobileSideBar={showMobileSideBar}
          toggleMobileSideBar={toggleMobileSideBar}
          onConnectWallet={onConnectWallet}
        />
      )}

      <RateLimitPopup openRateLimitPopup={rateExceeded} />

      {!useMinimalLayout && <BackOverlay />}

      {!useMinimalLayout && showChrome && (
        <ChatPanel open={openChat} setOpen={(open) => setOpenChat(open)} />
      )}

      {!useMinimalLayout && showChrome && <SideChatPanel />}
    </Box>
  );
};

const MainLayout = (props: React.PropsWithChildren) => (
  <LayoutChromeProvider>
    <MainLayoutContent {...props} />
  </LayoutChromeProvider>
);

export default MainLayout;
